import * as ActionTypes from '../actions/types';
// @ts-ignore
import { v4 } from 'uuid';
import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import config from '../utils/constants/config';
import { detectWarningsAction } from '../actions/message';
type State =
    | {
          byId: { [id: string]: any } | {};
          allIds: Array<string | any>;
          internalErrors: null | any;
          serviceErrors: null | any;
      }
    | any;
type Payload = {
    response: { body: { errors: any[]; title: string } };
    customMessage: string;
};

const createMessage = (
    type: string,
    message: string,
    description: string | null = null,
) => {
    const newId = v4();
    return {
        type: type,
        uuid: newId,
        message: message,
        created: new Date(),
        description: description,
    };
};

const INITIAL_STATE: State = {
    byId: {},
    allIds: [],
    internalErrors: null,
    serviceErrors: null,
};

const addSuccessMessage =
    (initialMessage = 'Action was successful') =>
    (state: State) => {
        const message = createMessage(
            config.StatusMessage.SUCCESS,
            initialMessage,
        );

        return {
            ...state,
            byId: {
                ...state.byId,
                [message.uuid]: message,
            },
            allIds: [...state.allIds, message.uuid],
        };
    };

const addErrorMessage =
    (initialMessage: string | undefined) =>
    (state: State, action: PayloadAction<Payload>) => {
        let text: any[] | string;
        if (
            action.payload &&
            action.payload.response &&
            action.payload.response.body &&
            action.payload.response.body.errors &&
            action.payload.response.body.errors.map((error: any) => error)
        ) {
            text = action.payload.response.body.errors.map(error => error);
        } else if (action.payload && action.payload.customMessage) {
            text = action.payload.customMessage;
        } else if (initialMessage) {
            text = initialMessage;
        } else if (
            action.payload &&
            action.payload.response &&
            action.payload.response.body &&
            action.payload.response.body.title
        ) {
            text = action.payload.response.body.title;
        } else {
            text = 'Action Failed';
        }

        const message = createMessage(
            config.StatusMessage.ERROR,
            text as string,
        );
        return {
            ...state,
            byId: {
                ...state.byId,
                [message.uuid]: message,
            },
            allIds: [...state.allIds, message.uuid],
        };
    };

const addWarningMessage = (initialMessage: string) => (state: State) => {
    const message = createMessage(config.StatusMessage.WARNING, initialMessage);
    return {
        ...state,
        byId: {
            ...state.byId,
            [message.uuid]: message,
        },
        allIds: [...state.allIds, message.uuid],
    };
};

const removeMessage = (
    state: { byId: { [x: string]: any } },
    action: { payload: { uuid: string } },
) => {
    const { [action.payload.uuid]: value, ...newMessages } = state.byId;

    return {
        ...state,
        byId: {
            ...newMessages,
        },
        allIds: [...Object.entries(newMessages).map(([k, v]) => v.uuid)],
    };
};

const detectWarnings = (
    state: State,
    action: PayloadAction<detectWarningsAction>,
) => {
    let warnings: any[] = [];
    if (
        action.payload &&
        action.payload.body &&
        action.payload.body.warnings &&
        action.payload.body.warnings.map((warning: string) => warning)
    ) {
        warnings = action.payload.body.warnings.map(
            (warning: string) => warning,
        );
    }

    if (warnings.length === 0) {
        return {
            ...state,
        };
    } else {
        const message = createMessage(
            config.StatusMessage.WARNING,
            warnings as any,
        );

        return {
            ...state,
            byId: {
                ...state.byId,
                [message.uuid]: message,
            },
            allIds: [...state.allIds, message.uuid],
        };
    }
};
const addInternalError = (
    state: any,
    action: { payload: { response: { body: { title: string } } } },
) => {
    return {
        ...state,
        internalErrors: action?.payload?.response?.body?.title,
    };
};

const addIServiceError = (
    state: any,
    action: { payload: { response: { body: { traceId: string } } } },
) => {
    return {
        ...state,
        serviceErrors: action.payload.response.body.traceId,
    };
};

const hideMessage = (state: State, action: any) => {
    return {
        ...state,
        byId: {},
        allIds: [],
    };
};

const message = createReducer(INITIAL_STATE, {
    [ActionTypes.REMOVE_MESSAGE]: removeMessage,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_SUCCESS]:
        addSuccessMessage('Bank details updated successfully'),
    [ActionTypes.SEND_CLAIM_APPLICATION_SUCCESS]: detectWarnings,
    [ActionTypes.HANDLE_BUSINESS_ERRORS]: addErrorMessage(undefined),
    [ActionTypes.HANDLE_INTERNAL_ERRORS]: addInternalError,
    [ActionTypes.HANDLE_SERVICE_ERRORS]: addIServiceError,
    [ActionTypes.HIDE_MESSAGE]: hideMessage,
    [ActionTypes.DISPLAY_DOWNLOAD_FILE_WARNING]: detectWarnings,
});

export default message;
